import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Layout from '../../../../components/Layout';
import BopBackLink from '../../../../components/BopBackLink';
import ApiInspector from '../../../../components/ApiInspector';
import { apiFetch } from '../../../../lib/api';
import { useFiltroSocieta } from '../../../../hooks/useFiltroSocieta';
import { usePermesso } from '../../../../context/PermessiContext';
import { useDirtyForm } from '../../../../hooks/useDirtyForm';
import { showToast } from '../../../../components/Toast';
import { formatQuantity } from '../../../../utils/formatters';
import { API_DOCS } from './WizardDdt.api';
import './WizardDdt.css';

// ── Tipi ──────────────────────────────────────────────────────────────────────

type TipoDdt = 'VENDITA' | 'CANTIERE';

interface CommessaRef {
  id: number;
  numero: string;
  descrizione: string | null;
}

// Riga come arriva dal backend (stessa forma per VENDITA e CANTIERE)
interface ApiRiga {
  rigaOrdineId: number | null;
  rigaOrdinePos: number | null;
  componenteId: number | null;
  rigaDistintaId: number | null;
  articoloId: number | null;
  codice: string | null;
  descrizione: string;
  unitaMisura: string | null;
  quantita: number | null;
  giaConsegnata: number;
  residuo: number | null;
}

interface ApiGenerabileVendita {
  ordineId: number;
  numero: string;
  stato: string;
  dataConferma: string | null;
  clienteRagioneSociale: string | null;
  commessa: CommessaRef | null;
  ddtEsistenti: number;
  righeResidue: number;
  qtaResidua: number | string;
  righe: ApiRiga[];
}

interface ApiGenerabileCantiere {
  distintaId: number;
  etichetta: string;
  ordineId: number;
  ordineNumero: string;
  commessa: CommessaRef | null;
  clienteRagioneSociale: string | null;
  confermataAt: string | null;
  righeResidue: number;
  qtaResidua: number | string;
  righe: ApiRiga[];
}

// Riga in editing locale (quantità come stringa con virgola)
interface RigaLocale {
  key: string;
  rigaOrdineId: number | null;
  rigaOrdinePos: number | null;
  componenteId: number | null;
  rigaDistintaId: number | null;
  codice: string | null;
  descrizione: string;
  unitaMisura: string | null;
  quantita: number | null;
  giaConsegnata: number;
  residuo: number | null;
  selezionata: boolean;
  qta: string;
}

// Elemento accordion: ordine (VENDITA) o distinta (CANTIERE)
interface ElementoLocale {
  id: number; // ordineId per VENDITA, distintaId per CANTIERE
  etichetta: string; // numero ordine / etichetta distinta
  ordineNumero: string | null; // solo CANTIERE: ordine di provenienza
  clienteRagioneSociale: string | null;
  commessa: CommessaRef | null;
  ddtEsistenti: number; // solo VENDITA (0 per CANTIERE)
  aperto: boolean;
  righe: RigaLocale[];
}

interface EsitoGenerazione {
  ordineId?: number;
  distintaId?: number;
  ordineNumero?: string;
  etichetta?: string;
  esito: 'CREATO' | 'SALTATO' | 'ERRORE';
  ddtId?: number;
  nRighe?: number;
  motivo?: string;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

// Converte una stringa con virgola decimale in number
function toNum(s: string): number {
  return Number(s.replace(',', '.'));
}

// Da number a stringa input con virgola
function numToInput(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(Number(n))) return '';
  return String(Number(n)).replace('.', ',');
}

// Estrae il messaggio dagli errori API ({error: string | {code,message}})
function estraiErrore(err: unknown, fallback: string): string {
  if (err && typeof err === 'object') {
    const payload = (err as { payload?: { error?: string | { code?: string; message?: string } } }).payload;
    const e = payload?.error;
    if (e && typeof e === 'object' && e.message) return e.message;
    if (typeof e === 'string' && e) return e;
    if (err instanceof Error && err.message && err.message !== '[object Object]') return err.message;
  }
  return fallback;
}

function badgeEsito(esito: EsitoGenerazione['esito']): React.ReactNode {
  switch (esito) {
    case 'CREATO':
      return <span className="bop-badge bop-badge-success">Creato</span>;
    case 'SALTATO':
      return <span className="bop-badge bop-badge-warning">Saltato</span>;
    case 'ERRORE':
      return <span className="bop-badge bop-badge-danger">Errore</span>;
    default:
      return <span className="bop-badge bop-badge-neutral">{esito}</span>;
  }
}

// Riga selezionabile = residuo non azzerato (null = quantità libera, sempre selezionabile)
function isSelezionabile(r: RigaLocale): boolean {
  return !(r.residuo !== null && r.residuo <= 0);
}

// Quantità di default = residuo (vuota se residuo assente)
function defaultQta(r: RigaLocale): string {
  return r.residuo !== null && r.residuo > 0 ? numToInput(r.residuo) : '';
}

// Filtro testo client-side su testata elemento
function matchQ(e: ElementoLocale, q: string): boolean {
  if (!q) return true;
  const campi = [e.etichetta, e.ordineNumero, e.clienteRagioneSociale, e.commessa?.numero, e.commessa?.descrizione];
  return campi.some(c => (c || '').toLowerCase().includes(q));
}

// Trasforma le righe API in righe locali con chiave univoca
function buildRigheLocali(righe: ApiRiga[], tipo: TipoDdt): RigaLocale[] {
  return (righe || []).map((r, i) => {
    const residuo = r.residuo !== null && r.residuo !== undefined ? Number(r.residuo) : null;
    const riga: RigaLocale = {
      key:
        tipo === 'CANTIERE'
          ? `d${r.rigaDistintaId ?? `i${i}`}`
          : r.componenteId !== null && r.componenteId !== undefined
            ? `c${r.componenteId}`
            : `r${r.rigaOrdineId ?? `i${i}`}`,
      rigaOrdineId: r.rigaOrdineId ?? null,
      rigaOrdinePos: r.rigaOrdinePos ?? null,
      componenteId: r.componenteId ?? null,
      rigaDistintaId: r.rigaDistintaId ?? null,
      codice: r.codice ?? null,
      descrizione: r.descrizione,
      unitaMisura: r.unitaMisura ?? null,
      quantita: r.quantita !== null && r.quantita !== undefined ? Number(r.quantita) : null,
      giaConsegnata: Number(r.giaConsegnata ?? 0),
      residuo,
      selezionata: false,
      qta: '',
    };
    riga.qta = defaultQta(riga);
    return riga;
  });
}

// ── Pagina ────────────────────────────────────────────────────────────────────

export default function WizardDdt() {
  const navigate = useNavigate();
  const { societaId, listaSocieta, isDisabled, pronta } = useFiltroSocieta();
  const { can, isSuperadmin } = usePermesso();
  const canEdit = can('commerciale.ddt', 'edit');

  // Stato locale società (solo SUPERADMIN con "Tutte" può modificarlo)
  const [societaIdLocale, setSocietaIdLocale] = useState('');
  const societaIdEff = societaId || societaIdLocale;

  // Tab attiva
  const [tab, setTab] = useState<TipoDdt>('VENDITA');

  // Elementi accordion della tab attiva (ricostruiti a ogni caricamento:
  // le selezioni si azzerano automaticamente al cambio società/tab/ricarica)
  const [elementi, setElementi] = useState<ElementoLocale[]>([]);
  const [loading, setLoading] = useState(false);

  // Ricerca testo client-side
  const [qInput, setQInput] = useState('');
  const [q, setQ] = useState('');

  // Generazione ed esiti
  const [salvando, setSalvando] = useState(false);
  const [esiti, setEsiti] = useState<EsitoGenerazione[] | null>(null);
  const [creati, setCreati] = useState(0);
  const [tipoEsiti, setTipoEsiti] = useState<TipoDdt>('VENDITA');

  // ── Riepilogo selezione ─────────────────────────────────────────────────────

  const totali = useMemo(() => {
    let righeSel = 0;
    let ddt = 0;
    for (const e of elementi) {
      const n = e.righe.filter(r => r.selezionata).length;
      if (n > 0) {
        ddt += 1;
        righeSel += n;
      }
    }
    return { righeSel, ddt };
  }, [elementi]);

  // Selezioni pendenti = lavoro non ancora salvato
  const isDirty = !esiti && totali.righeSel > 0;
  useDirtyForm(isDirty, 'Generazione massiva DDT');

  // Debounce ricerca testo (client-side)
  useEffect(() => {
    const t = setTimeout(() => setQ(qInput.trim().toLowerCase()), 300);
    return () => clearTimeout(t);
  }, [qInput]);

  // ── Caricamento elementi generabili ─────────────────────────────────────────

  const carica = useCallback(async () => {
    if (!pronta) return;
    setLoading(true);
    setElementi([]); // azzera subito righe e selezioni della vista precedente
    try {
      const params = new URLSearchParams();
      if (societaIdEff) params.set('societaId', societaIdEff);
      params.set('tipo', tab);

      let lista: ElementoLocale[] = [];
      if (tab === 'VENDITA') {
        // @api GET /api/ddt/generabili?societaId&tipo=VENDITA -> {"success":true,"data":[{"ordineId":"number","numero":"string","stato":"string","dataConferma":"string|null","clienteRagioneSociale":"string|null","commessa":{"id":"number","numero":"string","descrizione":"string|null"},"ddtEsistenti":"number","righeResidue":"number","qtaResidua":"number","righe":[{"rigaOrdineId":"number|null","rigaOrdinePos":"number|null","componenteId":"number|null","rigaDistintaId":"null","articoloId":"number|null","codice":"string|null","descrizione":"string","unitaMisura":"string|null","quantita":"number|null","giaConsegnata":"number","residuo":"number|null"}]}]}
        const r = await apiFetch<{ success: boolean; data: ApiGenerabileVendita[] }>(
          `/api/ddt/generabili?${params.toString()}`
        );
        lista = (r.data || []).map(o => ({
          id: o.ordineId,
          etichetta: o.numero,
          ordineNumero: null,
          clienteRagioneSociale: o.clienteRagioneSociale,
          commessa: o.commessa,
          ddtEsistenti: o.ddtEsistenti ?? 0,
          aperto: false,
          righe: buildRigheLocali(o.righe, 'VENDITA'),
        }));
      } else {
        // @api GET /api/ddt/generabili?societaId&tipo=CANTIERE -> {"success":true,"data":[{"distintaId":"number","etichetta":"string","ordineId":"number","ordineNumero":"string","commessa":{"id":"number","numero":"string","descrizione":"string|null"},"clienteRagioneSociale":"string|null","confermataAt":"string|null","righeResidue":"number","qtaResidua":"number","righe":[{"rigaDistintaId":"number","rigaOrdineId":"number|null","rigaOrdinePos":"number|null","componenteId":"number|null","articoloId":"number|null","codice":"string|null","descrizione":"string","unitaMisura":"string|null","quantita":"number|null","giaConsegnata":"number","residuo":"number"}]}]}
        const r = await apiFetch<{ success: boolean; data: ApiGenerabileCantiere[] }>(
          `/api/ddt/generabili?${params.toString()}`
        );
        lista = (r.data || []).map(d => ({
          id: d.distintaId,
          etichetta: d.etichetta,
          ordineNumero: d.ordineNumero || null,
          clienteRagioneSociale: d.clienteRagioneSociale,
          commessa: d.commessa,
          ddtEsistenti: 0,
          aperto: false,
          righe: buildRigheLocali(d.righe, 'CANTIERE'),
        }));
      }

      // Espansione di default quando gli elementi sono pochi
      if (lista.length > 0 && lista.length <= 3) {
        lista = lista.map(e => ({ ...e, aperto: true }));
      }
      setElementi(lista);
    } catch (err: unknown) {
      showToast(estraiErrore(err, 'Errore nel caricamento degli elementi generabili'), 'error');
      setElementi([]);
    } finally {
      setLoading(false);
    }
  }, [pronta, societaIdEff, tab]);

  useEffect(() => { carica(); }, [carica]);

  // Espandi di default gli accordion quando gli elementi visibili sono ≤3
  // (al termine del caricamento e a ogni cambio del filtro di ricerca)
  useEffect(() => {
    if (loading) return;
    setElementi(prev => {
      const visibili = prev.filter(e => matchQ(e, q));
      if (visibili.length === 0 || visibili.length > 3) return prev;
      const ids = new Set(visibili.map(e => e.id));
      if (!prev.some(e => ids.has(e.id) && !e.aperto)) return prev;
      return prev.map(e => (ids.has(e.id) ? { ...e, aperto: true } : e));
    });
  }, [q, loading]);

  // ── Filtro testo client-side ────────────────────────────────────────────────

  const elementiFiltrati = useMemo(() => elementi.filter(e => matchQ(e, q)), [elementi, q]);

  // ── Selezione e editing ─────────────────────────────────────────────────────

  const toggleAperto = (id: number) => {
    setElementi(prev => prev.map(e => (e.id === id ? { ...e, aperto: !e.aperto } : e)));
  };

  // Checkbox di testata: seleziona/deseleziona tutte le righe con residuo
  const toggleTestata = (id: number) => {
    setElementi(prev => prev.map(e => {
      if (e.id !== id) return e;
      const selezionabili = e.righe.filter(isSelezionabile);
      if (selezionabili.length === 0) return e;
      const tutteSelezionate = selezionabili.every(r => r.selezionata);
      return {
        ...e,
        righe: e.righe.map(r => {
          if (!isSelezionabile(r)) return r;
          if (tutteSelezionate) return { ...r, selezionata: false };
          return { ...r, selezionata: true, qta: r.qta || defaultQta(r) };
        }),
      };
    }));
  };

  const toggleRiga = (elId: number, key: string) => {
    setElementi(prev => prev.map(e => {
      if (e.id !== elId) return e;
      return {
        ...e,
        righe: e.righe.map(r => {
          if (r.key !== key) return r;
          const selezionata = !r.selezionata;
          return { ...r, selezionata, qta: selezionata && !r.qta ? defaultQta(r) : r.qta };
        }),
      };
    }));
  };

  const setQtaRiga = (elId: number, key: string, val: string) => {
    setElementi(prev => prev.map(e => {
      if (e.id !== elId) return e;
      return { ...e, righe: e.righe.map(r => (r.key === key ? { ...r, qta: val } : r)) };
    }));
  };

  // ── Generazione massiva ─────────────────────────────────────────────────────

  const creaDdt = async () => {
    // Validazione + costruzione payload a livello di singola riga
    const payloadElementi: Array<{
      ordineId?: number;
      distintaId?: number;
      righe: Array<{ rigaOrdineId?: number; componenteId?: number; rigaDistintaId?: number; quantita: number }>;
    }> = [];

    for (const el of elementi) {
      const righeSel = el.righe.filter(r => r.selezionata);
      if (righeSel.length === 0) continue; // manda SOLO elementi con almeno una riga selezionata

      const righePayload: Array<{ rigaOrdineId?: number; componenteId?: number; rigaDistintaId?: number; quantita: number }> = [];
      const dove = tab === 'VENDITA' ? `ordine ${el.etichetta}` : `distinta ${el.etichetta}`;

      for (const r of righeSel) {
        const qta = toNum(r.qta);
        if (!Number.isFinite(qta) || qta <= 0) {
          showToast(`Quantità da consegnare non valida per la riga "${r.descrizione}" (${dove}): inserire un valore maggiore di zero`, 'error');
          return;
        }
        if (r.residuo !== null && qta > r.residuo) {
          showToast(`La quantità da consegnare per la riga "${r.descrizione}" (${dove}) supera il residuo (${formatQuantity(r.residuo)})`, 'error');
          return;
        }
        if (tab === 'CANTIERE') {
          righePayload.push({ rigaDistintaId: r.rigaDistintaId as number, quantita: qta });
        } else if (r.componenteId !== null) {
          // Chiave riga VENDITA: componenteId se presente, altrimenti rigaOrdineId
          righePayload.push({ componenteId: r.componenteId, quantita: qta });
        } else {
          righePayload.push({ rigaOrdineId: r.rigaOrdineId as number, quantita: qta });
        }
      }

      payloadElementi.push(
        tab === 'VENDITA'
          ? { ordineId: el.id, righe: righePayload }
          : { distintaId: el.id, righe: righePayload }
      );
    }

    if (payloadElementi.length === 0) {
      showToast('Seleziona almeno una riga da consegnare', 'error');
      return;
    }

    setSalvando(true);
    try {
      // @api POST /api/ddt/genera-massivi {"tipo":"VENDITA|CANTIERE","elementi":[{"ordineId":"number?","distintaId":"number?","righe":[{"rigaOrdineId":"number?","componenteId":"number?","rigaDistintaId":"number?","quantita":"number"}]}]} -> {"success":true,"data":{"risultati":[{"ordineId":"number?","distintaId":"number?","ordineNumero":"string?","etichetta":"string?","esito":"CREATO|SALTATO|ERRORE","ddtId":"number?","nRighe":"number?","motivo":"string?"}],"creati":"number"}}
      const res = await apiFetch<{
        success: boolean;
        data?: { risultati: EsitoGenerazione[]; creati: number };
        error?: unknown;
      }>('/api/ddt/genera-massivi', {
        method: 'POST',
        body: JSON.stringify({ tipo: tab, elementi: payloadElementi }),
      });

      if (!res.success || !res.data) {
        showToast(estraiErrore({ payload: res }, 'Errore nella generazione dei DDT'), 'error');
        return;
      }

      setTipoEsiti(tab);
      setCreati(res.data.creati ?? 0);
      setEsiti(res.data.risultati || []);
      // Azzera le selezioni: il lavoro è concluso
      setElementi(prev => prev.map(e => ({ ...e, righe: e.righe.map(r => ({ ...r, selezionata: false })) })));

      if ((res.data.creati ?? 0) > 0) {
        showToast(
          res.data.creati === 1 ? '1 DDT creato in bozza' : `${res.data.creati} DDT creati in bozza`,
          'success'
        );
      }
    } catch (err: unknown) {
      showToast(estraiErrore(err, 'Errore nella generazione dei DDT'), 'error');
    } finally {
      setSalvando(false);
    }
  };

  const creaAltri = () => {
    setEsiti(null);
    setCreati(0);
    carica();
  };

  // ── Render accordion elemento ───────────────────────────────────────────────

  const renderAccordion = (el: ElementoLocale) => {
    const selezionabili = el.righe.filter(isSelezionabile);
    const nSelezionate = selezionabili.filter(r => r.selezionata).length;
    const tutteSelezionate = selezionabili.length > 0 && nSelezionate === selezionabili.length;
    const parzialmenteSelezionate = nSelezionate > 0 && !tutteSelezionate;

    return (
      <div className="wizard-ddt__accordion" key={el.id}>
        <div
          className="wizard-ddt__accordion-header"
          role="button"
          tabIndex={0}
          onClick={() => toggleAperto(el.id)}
          onKeyDown={e => {
            if (e.target !== e.currentTarget) return;
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              toggleAperto(el.id);
            }
          }}
        >
          <span className={`wizard-ddt__accordion-arrow ${el.aperto ? 'wizard-ddt__accordion-arrow--open' : ''}`}>
            ▸
          </span>
          <input
            type="checkbox"
            className="wizard-ddt__accordion-check"
            checked={tutteSelezionate}
            ref={node => { if (node) node.indeterminate = parzialmenteSelezionate; }}
            disabled={selezionabili.length === 0}
            onClick={e => e.stopPropagation()}
            onChange={() => toggleTestata(el.id)}
          />
          <span className="wizard-ddt__accordion-title">
            {tab === 'VENDITA' ? `Ordine ${el.etichetta}` : el.etichetta}
          </span>
          {tab === 'CANTIERE' && el.ordineNumero && (
            <span className="wizard-ddt__accordion-meta">Ordine {el.ordineNumero}</span>
          )}
          {el.clienteRagioneSociale && (
            <span className="wizard-ddt__accordion-meta">{el.clienteRagioneSociale}</span>
          )}
          {el.commessa && (
            <span className="wizard-ddt__accordion-meta">
              Commessa {el.commessa.numero}
              {el.commessa.descrizione ? ` — ${el.commessa.descrizione}` : ''}
            </span>
          )}
          {el.ddtEsistenti > 0 && (
            <span className="bop-badge bop-badge-info">
              {el.ddtEsistenti === 1 ? '1 DDT esistente' : `${el.ddtEsistenti} DDT esistenti`}
            </span>
          )}
          {selezionabili.length === 0 ? (
            <span className="wizard-ddt__accordion-count">
              <span className="bop-badge bop-badge-success">Tutto consegnato</span>
            </span>
          ) : (
            <span className="wizard-ddt__accordion-count">
              {selezionabili.length === 1 ? '1 riga selezionabile' : `${selezionabili.length} righe selezionabili`}
            </span>
          )}
        </div>
        {el.aperto && (
          <div className="wizard-ddt__accordion-body">
            {el.righe.length === 0 ? (
              <div className="bop-empty-state">Nessuna riga in questo elemento.</div>
            ) : (
              <table className="bop-table">
                <thead>
                  <tr>
                    <th className="bop-table__th--select"></th>
                    <th>Riga</th>
                    <th>Codice</th>
                    <th>Descrizione</th>
                    <th>U.M.</th>
                    <th className="bop-text-right">Q.tà</th>
                    <th className="bop-text-right">Già consegnata</th>
                    <th className="bop-text-right">Residuo</th>
                    <th className="bop-text-right">Q.tà da consegnare</th>
                  </tr>
                </thead>
                <tbody>
                  {el.righe.map(r => {
                    const consegnata = !isSelezionabile(r);
                    return (
                      <tr key={r.key} className={consegnata ? 'wizard-ddt__row--consegnata' : ''}>
                        <td className="bop-table__td--select">
                          <input
                            type="checkbox"
                            checked={r.selezionata}
                            disabled={consegnata}
                            onChange={() => toggleRiga(el.id, r.key)}
                          />
                        </td>
                        <td>{r.rigaOrdinePos ?? <span className="bop-text-muted">—</span>}</td>
                        <td>{r.codice || <span className="bop-text-muted">—</span>}</td>
                        <td>
                          {r.descrizione}
                          {consegnata && (
                            <span className="bop-badge bop-badge-neutral wizard-ddt__badge-consegnata">Consegnata</span>
                          )}
                        </td>
                        <td>{r.unitaMisura || <span className="bop-text-muted">—</span>}</td>
                        <td className="bop-text-right">
                          {r.quantita !== null ? formatQuantity(r.quantita) : <span className="bop-text-muted">—</span>}
                        </td>
                        <td className="bop-text-right">{formatQuantity(r.giaConsegnata)}</td>
                        <td className="bop-text-right">
                          {r.residuo !== null ? formatQuantity(r.residuo) : <span className="bop-text-muted">—</span>}
                        </td>
                        <td className="bop-text-right">
                          <input
                            className="bop-input wizard-ddt__qta-input"
                            value={r.selezionata ? r.qta : ''}
                            disabled={!r.selezionata}
                            onChange={e => setQtaRiga(el.id, r.key, e.target.value)}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    );
  };

  // ── Render pannello esiti ───────────────────────────────────────────────────

  const pannelloEsiti = esiti && (
    <div className="bop-card">
      <div className="bop-card__header">
        <h2 className="bop-card__title">Esito generazione</h2>
      </div>
      <div className="bop-card__body">
        <p className="wizard-ddt__esiti-summary">
          {creati === 1 ? '1 DDT creato in bozza' : `${creati} DDT creati in bozza`}{' '}
          su {esiti.length} {esiti.length === 1 ? 'elemento selezionato' : 'elementi selezionati'}.
        </p>
        <table className="bop-table">
          <thead>
            <tr>
              <th>Elemento</th>
              <th>Esito</th>
              <th>DDT</th>
              <th>Motivo</th>
            </tr>
          </thead>
          <tbody>
            {esiti.map((r, i) => (
              <tr key={`esito-${i}`}>
                <td>
                  <span className="wizard-ddt__numero">
                    {tipoEsiti === 'VENDITA'
                      ? `Ordine ${r.ordineNumero || r.ordineId || '—'}`
                      : r.etichetta || `Distinta ${r.distintaId || '—'}`}
                  </span>
                </td>
                <td>{badgeEsito(r.esito)}</td>
                <td>
                  {r.ddtId ? (
                    <Link className="wizard-ddt__ddt-link" to={`/commerciale/attivo/ddt/${r.ddtId}`}>
                      Apri bozza
                      {r.nRighe != null ? ` (${r.nRighe} ${r.nRighe === 1 ? 'riga' : 'righe'})` : ''}
                    </Link>
                  ) : (
                    <span className="bop-text-muted">—</span>
                  )}
                </td>
                <td>{r.motivo || <span className="bop-text-muted">—</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="wizard-ddt__esiti-actions">
          <button className="bop-btn bop-btn-secondary" onClick={creaAltri}>
            Crea altri
          </button>
          <button
            className="bop-btn bop-btn-primary"
            onClick={() => navigate('/commerciale/attivo/ddt')}
          >
            Vai ai DDT
          </button>
        </div>
      </div>
    </div>
  );

  // ── Render pagina ───────────────────────────────────────────────────────────

  return (
    <Layout>
      <div className="wizard-ddt">
        <BopBackLink to="/commerciale/attivo/ddt" label="Torna ai DDT" />

        {/* Header */}
        <div className="bop-page-header">
          <h1 className="bop-page-title">Genera DDT</h1>
        </div>

        {esiti ? (
          pannelloEsiti
        ) : (
          <>
            {/* Toggle tipo generazione */}
            <div className="bop-tabs">
              <button
                className={`bop-tab ${tab === 'VENDITA' ? 'active' : ''}`}
                onClick={() => setTab('VENDITA')}
              >
                DDT di vendita (da ordini)
              </button>
              <button
                className={`bop-tab ${tab === 'CANTIERE' ? 'active' : ''}`}
                onClick={() => setTab('CANTIERE')}
              >
                DDT su cantiere (da distinte)
              </button>
            </div>

            {/* Filtri */}
            <div className="bop-filter-bar wizard-ddt__filters">
              {!isDisabled && (
                <div className="bop-form-group">
                  <label className="bop-label">Società</label>
                  <select
                    className="bop-select"
                    value={societaIdLocale}
                    onChange={e => setSocietaIdLocale(e.target.value)}
                  >
                    {isSuperadmin && <option value="">Tutte</option>}
                    {listaSocieta.map(s => (
                      <option key={s.id} value={String(s.id)}>{s.nome}</option>
                    ))}
                  </select>
                </div>
              )}

              <div className="bop-form-group">
                <label className="bop-label">Cerca</label>
                <input
                  className="bop-input wizard-ddt__search-input"
                  value={qInput}
                  onChange={e => setQInput(e.target.value)}
                  placeholder={
                    tab === 'VENDITA'
                      ? 'Ordine, cliente, commessa...'
                      : 'Distinta, ordine, cliente, commessa...'
                  }
                />
              </div>
            </div>

            {/* Accordion per tab attiva */}
            {!pronta ? (
              <div className="bop-card">
                <div className="bop-empty-state">
                  Seleziona una società per visualizzare gli elementi generabili
                </div>
              </div>
            ) : (
              <>
                <div className="bop-card">
                  <div className="bop-card__header">
                    <h2 className="bop-card__title">
                      {tab === 'VENDITA' ? 'Ordini con righe da consegnare' : 'Distinte con righe da consegnare'}
                    </h2>
                  </div>
                  <div className="bop-card__body">
                    {loading ? (
                      <div className="bop-empty-state">Caricamento…</div>
                    ) : elementiFiltrati.length === 0 ? (
                      <div className="bop-empty-state">
                        <p className="bop-empty-state__text">
                          {tab === 'VENDITA'
                            ? 'Nessun ordine di vendita confermato con righe da consegnare.'
                            : 'Nessuna distinta confermata con righe da consegnare.'}
                        </p>
                      </div>
                    ) : (
                      elementiFiltrati.map(renderAccordion)
                    )}
                  </div>
                </div>

                {/* Riepilogo sticky */}
                <div className="bop-card wizard-ddt__footer">
                  <div className="bop-card__body">
                    <div className="wizard-ddt__footer-row">
                      <div className="wizard-ddt__footer-info">
                        <span className="wizard-ddt__footer-count">
                          {totali.ddt === 1
                            ? `Verrà creato 1 DDT in bozza (${totali.righeSel === 1 ? '1 riga selezionata' : `${totali.righeSel} righe selezionate`})`
                            : `Verranno creati ${totali.ddt} DDT in bozza (${totali.righeSel === 1 ? '1 riga selezionata' : `${totali.righeSel} righe selezionate`})`}
                        </span>
                        <span className="bop-text-muted wizard-ddt__footer-note">
                          Un DDT per ogni ordine/distinta — numero e movimento di magazzino all'emissione
                        </span>
                      </div>
                      <div className="wizard-ddt__footer-actions">
                        <button
                          className="bop-btn bop-btn-secondary"
                          onClick={() => navigate('/commerciale/attivo/ddt')}
                          disabled={salvando}
                        >
                          Annulla
                        </button>
                        <button
                          className="bop-btn bop-btn-primary"
                          onClick={creaDdt}
                          disabled={salvando || totali.ddt === 0 || !canEdit}
                        >
                          {salvando
                            ? 'Creazione…'
                            : totali.ddt === 1
                              ? 'Crea 1 DDT in bozza'
                              : `Crea ${totali.ddt} DDT in bozza`}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </>
        )}

        <ApiInspector docs={API_DOCS} />
      </div>
    </Layout>
  );
}
